import { Directive, ElementRef, input, Renderer2, signal, inject } from '@angular/core';

@Directive({
  selector: '[tooltip]',
  standalone: true,
  host: {
    '(mouseenter)': 'show()',
    '(mouseleave)': 'hide()',
    '[style.cursor]': '"pointer"',
  },
})
export class TooltipDirective {
  private renderer = inject(Renderer2);
  private el = inject(ElementRef);

  readonly tooltip = input.required<string>();
  readonly alignmentX = input<'left' | 'right'>();
  readonly alignmentY = input<'top' | 'center' | 'bottom'>('top');
  readonly distance = input<number>(8);

  // Hard coded styles as Directives can't include styles
  private static readonly TOOLTIP_STYLES = {
    position: 'fixed',
    'text-wrap': 'pretty',
    background: 'oklch(20% 0.12 268.2deg)',
    color: 'oklch(100% 0 0deg)',
    'padding-block': 'clamp(0.25rem, 0.25rem + 0vw, 0.25rem)',
    'padding-inline': 'clamp(0.5rem, 0.5rem + 0vw, 0.5rem)',
    border: '1px solid oklch(36% 0.13 259.25deg)',
    'border-radius': '4px',
    'font-size': 'clamp(1rem, 0.9643rem + 0.1786vw, 1.125rem)',
    width: `clamp(20ch, 100%, 50px)`,
    'pointer-events': 'none',
  } as const;

  private readonly toolTipElement = signal<HTMLElement | null>(null);
  private readonly tooltipId = `tooltip-${crypto.randomUUID()}`;

  show() {
    if (!this.tooltip() || this.toolTipElement()) return;

    const tooltipElement = this.renderer.createElement('div');
    this.renderer.setAttribute(tooltipElement, 'popover', 'manual');
    this.renderer.setAttribute(tooltipElement, 'id', this.tooltipId);
    this.renderer.setAttribute(tooltipElement, 'role', 'tooltip');
    this.renderer.setProperty(tooltipElement, 'textContent', this.tooltip());

    // Apply styles from constants
    Object.entries(TooltipDirective.TOOLTIP_STYLES).forEach(([property, value]) => {
      this.renderer.setStyle(tooltipElement, property, value);
    });

    // Set anchor on element
    const anchorName = `--anchor-${this.tooltipId}`;
    this.renderer.setStyle(this.el.nativeElement, 'anchor-name', anchorName);
    this.renderer.setAttribute(this.el.nativeElement, 'aria-describedby', this.tooltipId);
    // Apply anchor positioning to tooltip
    this.renderer.setStyle(tooltipElement, 'position-anchor', anchorName);
    this.renderer.setStyle(
      tooltipElement,
      'position-area',
      `${this.alignmentX() ? this.alignmentX() : ''} ${this.alignmentY()}`,
    ); // Default 'top', to cater for 'center bug'
    this.renderer.setStyle(tooltipElement, 'position-try-fallbacks', 'flip-block, flip-inline');

    // Adjust distance from anchor if needed
    this.renderer.setStyle(tooltipElement, 'margin', `${this.distance()}px`);

    // Render the tooltip
    this.renderer.appendChild(document.body, tooltipElement);
    (tooltipElement as any).showPopover();
    this.toolTipElement.set(tooltipElement);
  }

  hide() {
    const tooltipElement = this.toolTipElement();
    if (tooltipElement) {
      (tooltipElement as any).hidePopover();
      this.renderer.removeChild(document.body, tooltipElement);
      this.renderer.removeAttribute(this.el.nativeElement, 'aria-describedby');
      this.toolTipElement.set(null);
    }
  }
}
